import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Card } from '@magnetic/card'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Spinner } from '@magnetic/spinner'
import { Negative, Information } from '@/lib/icons'
import { getApplicationDiff } from '@/lib/api'

interface ResourceDiffProps {
  applicationId: string
}

export function ResourceDiff({ applicationId }: ResourceDiffProps) {
  const { data: diffResponse, isLoading: isDiffLoading, error: diffError } = useConnectQuery(
    getApplicationDiff,
    { id: applicationId },
    { enabled: !!applicationId, refetchInterval: 30000 }
  )

  return (
    <Flex direction="vertical" gap="md">
      <Heading size="section">Resource Diff</Heading>
      <Card style={{ boxShadow: 'none' }}>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          {isDiffLoading ? (
            <Flex align="center" justify="center" style={{ padding: 24 }}>
              <Spinner size="md" />
            </Flex>
          ) : diffError ? (
            <Flex direction="vertical" align="center" gap="sm" style={{ padding: 24 }}>
              <Negative width={32} height={32} />
              <Text size="p3" color="light">
                {diffError instanceof Error ? diffError.message : 'Failed to load resource diff'}
              </Text>
            </Flex>
          ) : (diffResponse?.diffs?.length ?? 0) > 0 ? (
            <Flex direction="vertical" gap="sm">
              {diffResponse?.diffs?.map((diff) => (
                <Card key={`${diff.kind}-${diff.namespace}-${diff.name}`} style={{ boxShadow: 'none' }}>
                  <Flex direction="vertical" gap="xs" style={{ padding: 12 }}>
                    <Flex align="center" justify="space-between" gap="sm">
                      <Text size="p3" weight="semi-bold">
                        {diff.kind}/{diff.name}
                      </Text>
                      <Text size="p4" color="light">{diff.inSync ? 'In Sync' : 'Out of Sync'}</Text>
                    </Flex>
                    {(diff.diff || diff.liveState || diff.targetState) ? (
                      <pre style={{
                        margin: 0,
                        padding: 12,
                        borderRadius: 6,
                        backgroundColor: 'var(--mds-color-theme-background-solid-secondary-normal)',
                        overflow: 'auto',
                        fontSize: '0.75rem',
                      }}>
                        {diff.diff || diff.targetState || diff.liveState}
                      </pre>
                    ) : (
                      <Text size="p4" color="light">No diff details available.</Text>
                    )}
                  </Flex>
                </Card>
              ))}
            </Flex>
          ) : (
            <Flex direction="vertical" align="center" gap="sm" style={{ padding: 24 }}>
              <Information width={28} height={28} />
              <Text size="p3" color="light">No resource diffs reported.</Text>
            </Flex>
          )}
        </Flex>
      </Card>
    </Flex>
  )
}
